import {
  Calendar,
  Clock,
  ArrowRight,
  Search,
  Tag,
} from "lucide-react";
import { Link } from "react-router-dom";
import Wrapper from "@/components/global/wrapper";
import Container from "@/components/global/container";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import {
  BLOG_POSTS,
  BLOG_CATEGORIES,
  formatBlogDate,
} from "@/constants/blog";

const BlogPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = [
    { id: "all", name: "All Posts", icon: Tag },
    ...BLOG_CATEGORIES,
  ];

  const featuredPost = useMemo(
    () => BLOG_POSTS.find((post) => post.featured) ?? BLOG_POSTS[0],
    [],
  );

  const filteredPosts = BLOG_POSTS.filter((post) => {
    const matchesCategory =
      selectedCategory === "all" || post.categoryId === selectedCategory;

    const normalizedQuery = searchQuery.trim().toLowerCase();
    const matchesSearch =
      normalizedQuery === "" ||
      post.title.toLowerCase().includes(normalizedQuery) ||
      post.excerpt.toLowerCase().includes(normalizedQuery) ||
      post.tags.some((tag) => tag.toLowerCase().includes(normalizedQuery));

    return matchesCategory && matchesSearch;
  });

  const gridPosts = useMemo(() => {
    if (!featuredPost) return filteredPosts;

    const showingFeaturedSeparately =
      selectedCategory === "all" && searchQuery.trim() === "";

    return showingFeaturedSeparately
      ? filteredPosts.filter((post) => post.id !== featuredPost.id)
      : filteredPosts;
  }, [featuredPost, filteredPosts, searchQuery, selectedCategory]);

  const formatDate = formatBlogDate;

  return (
    <Wrapper className="py-20 relative" data-oid="xzmo.l.">
      {/* Hero Section */}
      <Container data-oid="tu-kvjc">
        <div className="text-center max-w-4xl mx-auto mb-16" data-oid="-hnb4hx">
          <motion.h1
            className="text-4xl md:text-6xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-oid="s6f2tr."
          >
            Blyrha
            <span
              className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
              data-oid="svcom_a"
            >
              {" "}
              Blog
            </span>
          </motion.h1>
          <motion.p
            className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-oid=".675kxo"
          >
            Insights, tips, and strategies for AI-powered marketing success.
            Stay updated with the latest trends and best practices.
          </motion.p>
        </div>
      </Container>

      {/* Search and Categories */}
      <Container data-oid="5-q7zhc">
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          data-oid="crtv59p"
        >
          {/* Search Bar */}
          <div className="max-w-md mx-auto mb-8" data-oid="69ol05v">
            <div className="relative" data-oid="1hv2dbj">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
                data-oid="x-x634z"
              />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-3"
                data-oid="vc6q:mt"
              />
            </div>
          </div>

          {/* Categories */}
          <div
            className="flex flex-wrap justify-center gap-3 mb-12"
            data-oid="oayjvm."
          >
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <motion.button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-200 ${
                    selectedCategory === category.id
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-background hover:bg-muted border-border"
                  }`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                  data-oid="cg8jv7p"
                >
                  <Icon className="w-4 h-4" data-oid="j0dbzix" />
                  {category.name}
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </Container>

      {/* Featured Post */}
      <Container data-oid="6p3c3tt">
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid="-3krm0x"
        >
          <h2
            className="text-2xl font-bold mb-8 text-center"
            data-oid="17vyxu5"
          >
            Featured Article
          </h2>
          <div
            className="relative overflow-hidden rounded-2xl border bg-card hover:shadow-2xl transition-all duration-300"
            data-oid="hym70pl"
          >
            <div className="md:flex" data-oid="wtrrh5p">
              <div className="md:w-1/2" data-oid="_d9x388">
                {featuredPost && (
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-64 md:h-full object-cover"
                    onError={(e) => {
                      // Fallback to gradient if image fails
                      e.currentTarget.style.background =
                        "linear-gradient(135deg, #3b82f6, #8b5cf6)";
                      e.currentTarget.style.display = "block";
                    }}
                    data-oid="pfpyif3"
                  />
                )}
              </div>
              <div className="md:w-1/2 p-8" data-oid="e-xpgl3">
                {featuredPost && (
                  <>
                    <Badge className="mb-4" data-oid="7eb2lfk">
                      {featuredPost.category}
                    </Badge>
                    <h3 className="text-2xl font-bold mb-4" data-oid="kqcrpji">
                      {featuredPost.title}
                    </h3>
                    <p className="text-muted-foreground mb-6" data-oid="d4crp5f">
                      {featuredPost.excerpt}
                    </p>

                    <div
                      className="flex items-center gap-4 mb-6"
                      data-oid="c534o_a"
                    >
                      <div className="flex items-center gap-2" data-oid="1hpmsf9">
                        <img
                          src={featuredPost.author.avatar}
                          alt={featuredPost.author.name}
                          className="w-8 h-8 rounded-full"
                          onError={(e) => {
                            e.currentTarget.style.background = "#3b82f6";
                            e.currentTarget.style.display = "block";
                          }}
                          data-oid="ygjfog_"
                        />

                        <div data-oid="7drat6v">
                          <p className="text-sm font-medium" data-oid="o9d0h52">
                            {featuredPost.author.name}
                          </p>
                          <p
                            className="text-xs text-muted-foreground"
                            data-oid="sgp_oly"
                          >
                            {featuredPost.author.role}
                          </p>
                        </div>
                      </div>
                      <div
                        className="flex items-center gap-4 text-sm text-muted-foreground"
                        data-oid="eg2bn1d"
                      >
                        <div className="flex items-center gap-1" data-oid="kz0hl5v">
                          <Calendar className="w-4 h-4" data-oid="xsvfyzh" />
                          {formatDate(featuredPost.publishedAt)}
                        </div>
                        <div className="flex items-center gap-1" data-oid="fmbj4ac">
                          <Clock className="w-4 h-4" data-oid="815u3y3" />
                          {featuredPost.readTime}
                        </div>
                      </div>
                    </div>

                    <Button asChild className="group" data-oid="nfmcc44">
                      <Link to={`/blog/${featuredPost.slug}`}>
                        Read Full Article
                        <ArrowRight
                          className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
                          data-oid="xyt:qzj"
                        />
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </Container>

      {/* Blog Posts Grid */}
      <Container data-oid="6q._b0.">
        <div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          data-oid="56s0mon"
        >
          {gridPosts.map((post, index) => (
            <motion.article
              key={post.id}
              className="group overflow-hidden rounded-2xl border bg-card hover:shadow-lg transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              data-oid="f8nabnw"
            >
              <div className="relative overflow-hidden" data-oid="cx3xqya">
                <Link to={`/blog/${post.slug}`}>
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      // Fallback to gradient if image fails
                      e.currentTarget.style.background =
                        "linear-gradient(135deg, #3b82f6, #8b5cf6)";
                      e.currentTarget.style.display = "block";
                    }}
                    data-oid="9a-1dw8"
                  />
                </Link>

                <div className="absolute top-4 left-4" data-oid="btoo7.t">
                  <Badge variant="secondary" data-oid="lnkllo3">
                    {post.category}
                  </Badge>
                </div>
              </div>

              <div className="p-6" data-oid="s99d7uz">
                <Link
                  to={`/blog/${post.slug}`}
                  className="block"
                  data-oid="pe50bok"
                >
                  <h3 className="text-xl font-semibold mb-3 group-hover:text-blue-600 transition-colors">
                    {post.title}
                  </h3>
                </Link>
                <p
                  className="text-muted-foreground mb-4 text-sm leading-relaxed"
                  data-oid="6jr.tlj"
                >
                  {post.excerpt}
                </p>

                <div
                  className="flex items-center justify-between"
                  data-oid="0660bx8"
                >
                  <div className="flex items-center gap-2" data-oid="_j250t4">
                    <img
                      src={post.author.avatar}
                      alt={post.author.name}
                      className="w-6 h-6 rounded-full"
                      onError={(e) => {
                        e.currentTarget.style.background = "#3b82f6";
                        e.currentTarget.style.display = "block";
                      }}
                      data-oid=":2zhf1t"
                    />

                    <span
                      className="text-sm text-muted-foreground"
                      data-oid="hx0ix9v"
                    >
                      {post.author.name}
                    </span>
                  </div>
                  <div
                    className="flex items-center gap-3 text-xs text-muted-foreground"
                    data-oid="ixl124o"
                  >
                    <span data-oid="ua71ugt">
                      {formatDate(post.publishedAt)}
                    </span>
                    <span data-oid="1lb2xiq">•</span>
                    <span data-oid="k_i6sd0">{post.readTime}</span>
                  </div>
                </div>

                <Button
                  asChild
                  variant="ghost"
                  className="w-full mt-4 group-hover:bg-blue-50 transition-colors"
                  data-oid="8w69gym"
                >
                  <Link to={`/blog/${post.slug}`}>
                    Read More
                    <ArrowRight
                      className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
                      data-oid="jgdgd.x"
                    />
                  </Link>
                </Button>
              </div>
            </motion.article>
          ))}
        </div>
      </Container>

      {/* Newsletter Signup */}
      <Container data-oid="x3nrw7l">
        <motion.div
          className="text-center p-12 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 text-white mt-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-oid=".ni9ipc"
        >
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            data-oid="qfimboo"
          >
            Stay Updated
          </h2>
          <p
            className="text-xl mb-8 opacity-90 max-w-2xl mx-auto"
            data-oid="8ch6ksg"
          >
            Subscribe to our newsletter and get the latest AI marketing insights
            delivered to your inbox.
          </p>
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto"
            data-oid="lj82_g1"
          >
            <Input
              type="email"
              placeholder="Enter your email"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/70"
              data-oid="f2r_g1s"
            />

            <Button variant="secondary" className="px-8" data-oid=":lt47ct">
              Subscribe
            </Button>
          </div>
          <p className="text-sm opacity-70 mt-4" data-oid="s12vzu2">
            No spam. Unsubscribe anytime.
          </p>
        </motion.div>
      </Container>

      {/* No Results */}
      {gridPosts.length === 0 && (
        <Container data-oid="642yqjh">
          <motion.div
            className="text-center py-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-oid="qrko60p"
          >
            <Search
              className="w-12 h-12 text-muted-foreground mx-auto mb-4"
              data-oid="i_j-vv8"
            />
            <h3 className="text-xl font-semibold mb-2" data-oid="05.34av">
              No articles found
            </h3>
            <p className="text-muted-foreground mb-6" data-oid="-z4fv40">
              Try adjusting your search terms or category filter.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery("");
                setSelectedCategory("all");
              }}
              data-oid="94dgxvl"
            >
              Clear Filters
            </Button>
          </motion.div>
        </Container>
      )}
    </Wrapper>
  );
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export default BlogPage;
